import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Upload, FileText, Trash2, Edit2, Search, Loader2, CheckCircle2, XCircle, RefreshCw, BookOpenText } from "lucide-react";
import DocumentReader from "../components/DocumentReader";
import { toast } from "sonner";

const STATUS_ICON = {
  processing: <Loader2 className="animate-spin text-amber-500" size={14} />,
  ready: <CheckCircle2 className="text-emerald-500" size={14} />,
  error: <XCircle className="text-red-500" size={14} />,
};

export default function Documents() {
  const { user } = useAuth();
  const canEdit = user?.role !== "viewer";
  const [docs, setDocs] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reading, setReading] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/documents"); setDocs(data); } catch (err) { console.error("[ARIA]", err); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("description", "");
      try {
        const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data?.file_error) toast.warning(`${file.name}: ${data.file_error}`);
        else toast.success(`Added ${file.name}`);
      } catch (e) {
        toast.error(`Failed: ${file.name}`);
      }
    }
    setBusy(false);
    load();
  };

  const onDelete = async (id, name) => {
    if (!confirm(`Remove "${name}"?`)) return;
    await api.delete(`/documents/${id}`);
    toast.success("Removed");
    load();
  };

  const [reindexing, setReindexing] = useState(null);
  const onReindex = async (id, name) => {
    if (!confirm(`Re-index "${name}"?\n\nARIA will re-chunk this paper with the current indexing settings. Existing chunks will be replaced.`)) return;
    setReindexing(id);
    try {
      await api.post(`/documents/${id}/reindex`);
      toast.success(`Re-indexed "${name}"`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-index failed");
    } finally { setReindexing(null); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    await api.put(`/documents/${editing.document_id}`, {
      name: editing.name, description: editing.description
    });
    toast.success("Updated");
    setEditing(null);
    load();
  };

  const onDrop = (e) => {
    e.preventDefault();
    upload(Array.from(e.dataTransfer.files));
  };

  const filtered = docs.filter(d =>
    !search ||
    d.name.toLowerCase().includes(search.toLowerCase()) ||
    (d.description || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="h-screen overflow-y-auto p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl mb-1">Research</h1>
            <p className="text-sm text-[var(--aria-text-muted)]">Upload papers — ARIA extracts concepts, chunks, and answers from them.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-[var(--aria-text-muted)]" />
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search papers…"
                data-testid="doc-search"
                className="pl-8 pr-3 py-2 text-sm rounded-md border border-[var(--aria-border)] bg-[var(--aria-surface)] outline-none focus:ring-2 focus:ring-[var(--aria-primary)]" />
            </div>
            {canEdit && (
              <button onClick={() => fileRef.current?.click()} disabled={busy}
                data-testid="upload-button"
                className="flex items-center gap-2 px-4 py-2 rounded-md bg-[var(--aria-primary)] text-white hover:bg-[var(--aria-primary-hover)] text-sm font-medium disabled:opacity-50">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                Upload paper
              </button>
            )}
            <input ref={fileRef} type="file" multiple accept=".pdf,application/pdf" hidden
              onChange={e => upload(Array.from(e.target.files))} data-testid="file-input" />
          </div>
        </div>

        {canEdit && (
          <div onDragOver={e=>e.preventDefault()} onDrop={onDrop}
            className="aria-card border-dashed border-2 border-[var(--aria-border)] p-10 text-center mb-8">
            <Upload className="mx-auto mb-3 text-[var(--aria-primary)]" size={32} />
            <div className="text-sm">Drag & drop research PDFs here — or click <strong>Upload paper</strong> above.</div>
            <div className="text-xs text-[var(--aria-text-muted)] mt-1">Bulk upload supported · ARIA will extract text and concepts automatically.</div>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map(d => (
            <div key={d.document_id} className="aria-card p-5" data-testid={`doc-card-${d.document_id}`}>
              <div className="flex items-start justify-between mb-3">
                <FileText size={20} className="text-[var(--aria-primary)]" />
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider">
                  {STATUS_ICON[d.status]} {d.status}
                </div>
              </div>
              <h3 className="font-display text-lg leading-tight mb-1 truncate" title={d.name}>{d.name}</h3>
              <p className="text-xs text-[var(--aria-text-muted)] mb-4 line-clamp-2 min-h-[2em]">{d.description || "No description"}</p>
              <div className="grid grid-cols-2 gap-2 text-xs text-[var(--aria-text-muted)] mb-4">
                <div>Pages: <span className="text-[var(--aria-text)] font-medium">{d.page_count}</span></div>
                <div>Chunks: <span className="text-[var(--aria-text)] font-medium">{d.chunk_count}</span></div>
                <div className="col-span-2">Added: {new Date(d.created_at).toLocaleDateString()}</div>
              </div>
              {d.status === "error" && d.error_message && (
                <div className="text-xs text-red-600 mb-3">{d.error_message}</div>
              )}
              {d.file_error && (
                <div className="text-[11px] text-amber-500 mb-3 flex items-start gap-1.5" data-testid={`doc-file-warning-${d.document_id}`}>
                  <XCircle size={12} className="mt-0.5 shrink-0" /> Original file not stored — text still searchable.
                </div>
              )}
              <button onClick={() => setReading(d)} data-testid={`doc-read-${d.document_id}`} disabled={d.status === "processing"}
                className="w-full mb-2 px-3 py-2 text-xs rounded-md bg-[var(--aria-primary)]/10 text-[var(--aria-primary)] hover:bg-[var(--aria-primary)]/20 flex items-center justify-center gap-1.5 disabled:opacity-50 transition-colors">
                <BookOpenText size={12} /> Read &amp; ask ARIA
              </button>
              {canEdit && (
                <div className="flex gap-2">
                  <button onClick={() => setEditing({ ...d })} data-testid={`doc-edit-${d.document_id}`}
                    className="flex-1 px-3 py-1.5 text-xs rounded-md border border-[var(--aria-border)] hover:bg-slate-50 dark:hover:bg-white/5 flex items-center justify-center gap-1.5">
                    <Edit2 size={12} /> Edit
                  </button>
                  <button onClick={() => onReindex(d.document_id, d.name)} data-testid={`doc-reindex-${d.document_id}`}
                    disabled={reindexing === d.document_id || d.status === "processing"}
                    title={d.last_reindexed_at ? `Last re-indexed ${new Date(d.last_reindexed_at).toLocaleString()}` : "Re-chunk with current indexing settings"}
                    className="flex-1 px-3 py-1.5 text-xs rounded-md border border-[var(--aria-border)] hover:border-[var(--aria-primary)] hover:text-[var(--aria-primary)] disabled:opacity-50 flex items-center justify-center gap-1.5 transition-colors">
                    <RefreshCw size={12} className={reindexing === d.document_id ? "animate-spin" : ""} />
                    {reindexing === d.document_id ? "Re-indexing…" : "Re-index"}
                  </button>
                  <button onClick={() => onDelete(d.document_id, d.name)} data-testid={`doc-delete-${d.document_id}`}
                    className="px-3 py-1.5 text-xs rounded-md border border-red-200 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center justify-center gap-1.5">
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-[var(--aria-text-muted)]">
              No research papers yet — upload your first PDF to get started.
            </div>
          )}
        </div>
      </div>

      <DocumentReader open={!!reading} docId={reading?.document_id} citation={reading ? { name: reading.name } : null} onClose={() => setReading(null)} />

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div onClick={e=>e.stopPropagation()} className="aria-card p-6 max-w-md w-full" data-testid="edit-modal">
            <h3 className="font-display text-xl mb-4">Edit paper</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">Name</label>
                <input value={editing.name} onChange={e=>setEditing({...editing, name:e.target.value})}
                  className="w-full mt-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[var(--aria-primary)]" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">Description</label>
                <textarea value={editing.description||""} onChange={e=>setEditing({...editing, description:e.target.value})} rows={3}
                  className="w-full mt-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[var(--aria-primary)]" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm rounded-md border border-[var(--aria-border)]">Cancel</button>
              <button onClick={saveEdit} data-testid="edit-save" className="px-4 py-2 text-sm rounded-md bg-[var(--aria-primary)] text-white hover:bg-[var(--aria-primary-hover)]">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
